import {
  Component,
  input,
  output,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { ErsClickableDirective } from '@rcs/shared';

export interface MonthYearChange {
  month: number;
  year: number;
}

@Component({
  selector: 'ers-month-year-picker',
  standalone: true,
  imports: [
    TranslocoPipe,CommonModule, MatIconModule, ErsClickableDirective],
  templateUrl: './month-year-picker.component.html',
  styleUrls: ['./month-year-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthYearPickerComponent {
  /** Zero-based month index (0 = January … 11 = December). */
  currentMonth = input<number>(new Date().getMonth());

  /** Full four-digit year. */
  currentYear = input<number>(new Date().getFullYear());

  /** 'default' = prev/next + popup; 'compact' = inline selects */
  mode = input<'default' | 'compact'>('default');

  /**
   * Emitted whenever the selected month or year changes, either via
   * the prev/next buttons or the dropdown picker cells.
   */
  dateChange = output<MonthYearChange>();

  showPicker = false;

  readonly months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  readonly years = Array.from(
    { length: 9 },
    (_, i) => new Date().getFullYear() - 4 + i,
  );

  get displayDate(): string {
    return `${this.months[this.currentMonth()]} ${this.currentYear()}`;
  }

  /** Close the picker when the user clicks outside the host element. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const host = (event.target as HTMLElement).closest('ers-month-year-picker');
    if (!host) {
      this.showPicker = false;
    }
  }

  togglePicker(): void {
    this.showPicker = !this.showPicker;
  }

  selectMonth(monthIndex: number): void {
    this.showPicker = false;
    this.dateChange.emit({ month: monthIndex, year: this.currentYear() });
  }

  selectYear(year: number): void {
    this.showPicker = false;
    this.dateChange.emit({ month: this.currentMonth(), year });
  }

  prevMonth(): void {
    let month = this.currentMonth() - 1;
    let year  = this.currentYear();
    if (month < 0) { month = 11; year--; }
    this.dateChange.emit({ month, year });
  }

  nextMonth(): void {
    let month = this.currentMonth() + 1;
    let year  = this.currentYear();
    if (month > 11) { month = 0; year++; }
    this.dateChange.emit({ month, year });
  }

  onCompactMonthChange(event: Event): void {
    const month = Number((event.target as HTMLSelectElement).value);
    this.dateChange.emit({ month, year: this.currentYear() });
  }

  onCompactYearChange(event: Event): void {
    const year = Number((event.target as HTMLSelectElement).value);
    this.dateChange.emit({ month: this.currentMonth(), year });
  }
}
